/**
 * FE-only presentation rules for the cargo definitions editor — slimmed
 * down after the variants + property-groups refactor.
 *
 * <p>
 * History: legacy `CARGO_TYPE_CONFIGS` had per-type gating (which property
 * groups appear, which fields render, default UoM). That's all gone now:
 * groups + UoMs are universal across cargo types (operator picks whatever
 * applies), variant-derived properties (dimensions, livestock species,
 * temperature bands) move to the variant catalogue, and type-specific
 * "what does the operator see?" rules live on the variant catalogue
 * (<c>VARIANTS_BY_TYPE</c> in <c>@my/tms-api</c>).
 * </p>
 *
 * <p>
 * What's left in this file are the operator-visible labels for the seven
 * property groups (used by the chip row + grid badges) and a tiny helper
 * describing what each group will prompt for at order entry. The mock
 * page <c>MockCargoDefinitionsPage</c> carried the same data inline; this
 * is the production source of truth that both the real editor + the mock
 * pull from (once we delete the mock).
 * </p>
 */

import {
  PROPERTY_GROUP_KEYS,
  type CargoPropertyGroupsPayload,
  type CargoTypeValue,
  type PropertyGroupKey,
} from '@my/tms-api';

export { PROPERTY_GROUP_KEYS };
export type { PropertyGroupKey };

/**
 * Domain rule: which other cargo types' definitions can be added as
 * line items when a given type sits at the top of an order. Aggregator
 * types (FullLoad / PartLoad) carry the line items themselves; Container
 * supports groupage (Pallets inside the container); everything else is
 * a leaf (the cargo IS the unit).
 *
 * <p>
 * Currently consumed only by the editor's order-line preview pane.
 * Lifts up to `@my/tms-api` when the real order screen needs it.
 * </p>
 */
export const ALLOWED_LINE_TYPES: Record<CargoTypeValue, CargoTypeValue[]> = {
  FullLoad: [
    'Pallet',
    'BulkMaterials',
    'BulkLiquids',
    'AgriGrains',
    'Construction',
    'Pharmaceutical',
    'Livestock',
  ],
  PartLoad: ['Pallet', 'Construction', 'Pharmaceutical'],
  Container: ['Pallet'],
  IsoTank: [],
  Pallet: [],
  BulkMaterials: [],
  BulkLiquids: [],
  Livestock: [],
  AgriGrains: [],
  Construction: [],
  Pharmaceutical: [],
};

/** Operator-visible labels for the seven property groups. */
export const PROPERTY_GROUP_LABELS: Record<PropertyGroupKey, string> = {
  capacity: 'Max Capacity',
  handling: 'Handling',
  temperature: 'Temperature Controlled',
  stack: 'Stack Height',
  bic: 'BIC Code',
  compartments: 'Compartments',
  compliance: 'Compliance',
};

/** Tiny helper text shown under each expanded group block. */
export const PROPERTY_GROUP_HELPER: Record<PropertyGroupKey, string> = {
  capacity:
    'Set limits to apply to every order. Operators can override on the order line with reason.',
  handling: 'Default notes pre-fill on the order line. Operators can override per shipment.',
  temperature:
    'Fill in to apply this range to every order. Leave blank to ask the operator on each order. Override allowed on the order line.',
  stack: 'Set the stack constraint here. Override per order if needed.',
  bic: 'Always prompted at order entry — one BIC per physical unit.',
  compartments:
    'Ticking this chip declares this cargo supports compartment splitting. How to split is decided per shipment at order entry.',
  compliance:
    'Standard is set here. Cert Ref + Expiry can be set here (applied to every order) or left blank (asked per shipment). Override on the order line if needed.',
};

/** Short summary of what each group prompts for at order entry — used in previews. */
export function describePromptedGroup(group: PropertyGroupKey): string {
  switch (group) {
    case 'capacity':
      return 'order line asks for max weight / volume / pallets';
    case 'handling':
      return 'order line asks for handling notes';
    case 'temperature':
      return 'order line asks for min / max °C + max transit days';
    case 'stack':
      return 'order line asks for max stack height + double-stack y/n';
    case 'bic':
      return 'order line asks for BIC identifier (per unit)';
    case 'compartments':
      return 'order line asks how to split compartments';
    case 'compliance':
      return 'order line asks for missing fields (Standard / Cert Ref / Expiry)';
  }
}

/**
 * One-line summary of a group's filled-in values — used in the order
 * line preview to surface "what's inherited from this definition" so
 * the operator can see at a glance what the order form will lock in.
 */
export function describeFilledGroup(
  group: PropertyGroupKey,
  payload: CargoPropertyGroupsPayload,
): string {
  switch (group) {
    case 'capacity': {
      const c = payload.capacity ?? {};
      const parts: string[] = [];
      if (c.maxWeightKg != null) parts.push(`max ${c.maxWeightKg}kg`);
      if (c.maxVolumeM3 != null) parts.push(`max ${c.maxVolumeM3}m³`);
      if (c.maxPallets != null) parts.push(`max ${c.maxPallets} pallets`);
      return parts.join(', ');
    }
    case 'handling':
      return payload.handling?.notes ? `“${payload.handling.notes}”` : '';
    case 'temperature': {
      const t = payload.temperature ?? {};
      const parts: string[] = [];
      if (t.minTempC != null || t.maxTempC != null) {
        parts.push(`${t.minTempC ?? '?'}…${t.maxTempC ?? '?'}°C`);
      }
      if (t.maxTransitDays != null) parts.push(`max ${t.maxTransitDays} days`);
      return parts.join(', ');
    }
    case 'stack': {
      const s = payload.stack ?? {};
      const parts: string[] = [];
      if (s.maxStackHeightCm != null) parts.push(`max ${s.maxStackHeightCm}cm`);
      parts.push(s.doubleStackPermitted ? 'double-stack OK' : 'no double-stack');
      return parts.join(', ');
    }
    case 'compartments':
      return 'splittable';
    case 'compliance': {
      const c = payload.compliance ?? {};
      const parts: string[] = [];
      if (c.standard) parts.push(c.standard);
      if (c.certRef) parts.push(`cert ${c.certRef}`);
      if (c.expiryDate) parts.push(`exp ${c.expiryDate}`);
      return parts.join(' · ');
    }
    case 'bic':
      return '';
  }
}
